<template>
  <div class="match-predictions text-xs px-1 pb-2 grid gap-4">
    <!-- team markets: home vs away bars -->
    <div v-if="teamRows.length" class="grid gap-2.5">
      <div class="flex justify-between text-[10px] uppercase tracking-wide font-semibold text-gray-500">
        <span class="truncate">{{ homeName }}</span>
        <span class="truncate text-right">{{ awayName }}</span>
      </div>
      <div v-for="row in teamRows" :key="row.label">
        <div class="flex justify-between mb-0.5 tabular-nums">
          <b class="text-gray-800">{{ pct(row.home) }}</b>
          <span class="text-gray-500">{{ row.label }}</span>
          <b class="text-gray-800">{{ pct(row.away) }}</b>
        </div>
        <div class="flex h-1.5 gap-0.5" aria-hidden="true">
          <span class="flex justify-end flex-1 bg-gray-200 rounded-l">
            <span class="block h-full rounded-l bg-[#E59C98]" :style="{ width: (row.home || 0) + '%' }"></span>
          </span>
          <span class="flex-1 bg-gray-200 rounded-r">
            <span class="block h-full rounded-r bg-[#BDBDFB]" :style="{ width: (row.away || 0) + '%' }"></span>
          </span>
        </div>
      </div>
    </div>

    <!-- match markets: both sides of each one, each with its price and a + -->
    <div v-if="matchRows.length" class="grid gap-1.5">
      <div class="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Match</div>
      <div
        v-for="row in matchRows"
        :key="row.label"
        class="bg-white border border-gray-200 rounded-lg px-2.5 py-2"
      >
        <div class="text-center text-gray-500 mb-1">{{ row.label }}</div>
        <div class="grid grid-cols-2 gap-3">
          <div
            v-for="(leg, i) in row.legs"
            :key="leg.name"
            class="flex items-center gap-2"
            :class="{ 'flex-row-reverse': i === 1 }"
          >
            <div class="min-w-0" :class="{ 'text-right': i === 1 }">
              <div class="flex items-baseline gap-1 tabular-nums" :class="{ 'flex-row-reverse': i === 1 }">
                <span class="text-gray-800 font-semibold">{{ leg.name }}</span>
                <b :class="leg.value >= 50 ? 'text-[#22c55e]' : 'text-gray-700'">{{ pct(leg.value) }}</b>
              </div>
              <div class="text-[10px] text-gray-500 tabular-nums truncate">
                <template v-if="leg.odds">{{ leg.bookmaker || "best" }} <b class="text-gray-800">{{ leg.odds.toFixed(2) }}</b></template>
                <template v-else-if="leg.value > 0">fair <b class="text-gray-800">{{ (100 / leg.value).toFixed(2) }}</b></template>
              </div>
            </div>
            <AddPill v-if="slipEnabled" :selection="leg.selection" />
          </div>
        </div>
        <div class="flex h-1.5 gap-0.5 mt-1.5" aria-hidden="true">
          <span
            v-for="(leg, i) in row.legs"
            :key="leg.name"
            class="block h-full"
            :class="[i === 0 ? 'rounded-l' : 'rounded-r', leg.value >= 50 ? 'bg-[#22c55e]' : 'bg-gray-300']"
            :style="{ width: leg.value + '%' }"
          ></span>
        </div>
      </div>
    </div>

    <!-- most likely scores -->
    <div v-if="scorelines.length" class="grid gap-1.5">
      <div class="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Most likely scores</div>
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="s in scorelines"
          :key="s.score"
          class="px-2.5 py-1 rounded-full bg-gray-800/5 text-gray-700 tabular-nums"
          ><b class="text-gray-800">{{ s.score }}</b> · {{ s.value }}</span
        >
      </div>
    </div>

    <p v-if="!teamRows.length && !matchRows.length" class="text-center text-gray-500">
      No predictions for this match yet.
    </p>
  </div>
</template>

<script>
import AddPill from "./AddPill.vue";
import { bestGoalPrice } from "./bestPrice";

export default {
  name: "MatchPredictions",
  components: { AddPill },
  props: {
    match: { type: Object, required: true }, // the list card (predictions, markets)
    homeName: { type: String, default: "Home" },
    awayName: { type: String, default: "Away" },
    slipEnabled: { type: Boolean, default: false },
    slipMatch: { type: Object, required: true },
  },
  computed: {
    p() {
      const p = this.match.predictions || {};
      return { ...(p.sport_monks || {}), ...p };
    },
    teamRows() {
      return [
        ["Clean sheet", "HT_clean_sheet", "AT_clean_sheet"],
        ["To score", "HT_over_0_5", "AT_over_0_5"],
        ["To score 2+", "HT_over_1_5", "AT_over_1_5"],
      ]
        .map(([label, h, a]) => ({ label, home: this.num(this.p[h]), away: this.num(this.p[a]) }))
        .filter((r) => r.home !== null || r.away !== null);
    },
    matchRows() {
      const markets = this.match.markets || {};
      const rows = [
        {
          label: "Both teams to score",
          value: this.num(this.p.btts),
          market: markets.btts,
          marketKey: "btts",
          legs: [
            ["Yes", "yes", "Both teams to score"],
            ["No", "no", "Both teams to score: no"],
          ],
          key: "btts",
          line: "",
        },
      ];
      for (const line of ["2.5", "3.5", "4.5"]) {
        const key = line.replace(".", "_");
        rows.push({
          label: `Goals ${line}`,
          value: this.num(this.p[`FT_over_${key}`]),
          market: markets[`ou_${key}`],
          marketKey: `ou_${key}`,
          legs: [
            ["Over", "over", `Over ${line} goals`],
            ["Under", "under", `Under ${line} goals`],
          ],
          key: "ou",
          line,
        });
      }
      return rows
        .filter((r) => r.value !== null)
        .map((r) => ({
          label: r.label,
          legs: r.legs.map(([name, subject, label], i) => {
            const m = r.market && r.market[subject];
            // the public feed sends these as strings ("1.61"), so coerce before formatting;
            // without a price for the second side it is what's left of the first
            const value = m && Number(m.p) ? Math.round(Number(m.p)) : i === 0 ? r.value : 100 - r.value;
            // the best price any bookmaker offers, as on the slip
            const price = bestGoalPrice(this.match, r.marketKey, subject);
            return {
              name,
              value,
              odds: price ? price.odds : null,
              bookmaker: price ? price.bookmaker : null,
              selection: this.selection(r.key, subject, r.line, label),
            };
          }),
        }));
    },
    scorelines() {
      return (this.p.most_likely_score_lines || this.p.most_likely_scorelines || []).slice(0, 3);
    },
  },
  methods: {
    num(v) {
      const n = v === null || v === undefined || v === "" ? NaN : Number(v);
      return Number.isFinite(n) ? Math.round(n) : null; // old feeds use "-" for "no number"
    },
    pct(v) {
      return v === null || v === undefined ? "–" : `${Math.round(v)}%`;
    },
    selection(market, subject, line, label) {
      return {
        id: `${this.slipMatch.match_id}|${market}|${subject}|${line}`,
        label,
        match: this.slipMatch.title,
        match_id: this.slipMatch.match_id,
        kickoff: this.slipMatch.kickoff,
        side: null,
      };
    },
  },
};
</script>
